// exportUtils.js
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

function getBase64FromUrl(url, opacity = 0.1) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "Anonymous";
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext("2d");

      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.globalAlpha = opacity;
      ctx.drawImage(img, 0, 0);

      const base64 = canvas.toDataURL("image/png");
      resolve(base64);
    };
    img.onerror = () => reject(new Error("Image load error"));
    img.src = url;
  });
}

export async function downloadPDF(filteredSchoolsWithRank) {
  if (filteredSchoolsWithRank.length === 0) {
    alert("No filtered data to export.");
    return;
  }

  // Use your local logo path from public folder (React serves public files at root)
  const logoUrl = "/LogoEFWS.png";

  let logoBase64;
  try {
    logoBase64 = await getBase64FromUrl(logoUrl, 0.1);
  } catch (error) {
    console.error("Could not load logo for watermark", error);
  }

  const doc = new jsPDF();

  const columns = [
    { header: "Filtered Rank", dataKey: "filteredRank" },
    { header: "Name", dataKey: "name" },
    { header: "Original Rank", dataKey: "rank" },
    { header: "City/Town", dataKey: "city" },
    { header: "Type", dataKey: "NFTYPE" },
    { header: "Gender", dataKey: "gender" },
    { header: "Age Range", dataKey: "ageRange" },
    { header: "Pupils", dataKey: "pupilsKS4" },
    { header: "Grade 5+ %", dataKey: "grade5Plus" },
    { header: "Progress 8", dataKey: "progress8Score" },
  ];

  const data = filteredSchoolsWithRank.map((school) => ({
    filteredRank: school.filteredRank,
    name: school.name,
    rank: school.rank,
    city: school.city,
    NFTYPE: school.NFTYPE,
    gender: school.gender,
    ageRange: school.ageRange,
    pupilsKS4: school.pupilsKS4,
    grade5Plus: school.grade5Plus,
    progress8Score: school.progress8Score,
  }));

  doc.text("Filtered Schools Data", 14, 15);

  autoTable(doc, {
    startY: 20,
    head: [columns.map((col) => col.header)],
    body: data.map((row) => columns.map((col) => row[col.dataKey] ?? "")),
    styles: { fontSize: 8 },
    headStyles: { fillColor: [41, 157, 143] },
    theme: "striped",
    margin: { top: 10 },
    didDrawPage: (dataArg) => {
      if (logoBase64) {
        const pageWidth = doc.internal.pageSize.getWidth();
        const pageHeight = doc.internal.pageSize.getHeight();

        const logoWidth = 60;
        const logoHeight = 60;

        // Positions for 3 watermarks (top-center, center, bottom-center)
        const positions = [
          { x: pageWidth * 0.25 - logoWidth / 2, y: pageHeight * 0.2 - logoHeight / 2 }, // left-center
          { x: pageWidth * 0.5 - logoWidth / 2, y: pageHeight * 0.5 - logoHeight / 2 },  // center
          { x: pageWidth * 0.75 - logoWidth / 2, y: pageHeight * 0.8 - logoHeight / 2 }, // right-center
        ];


        positions.forEach(({ x, y }) => {
          doc.addImage(
            logoBase64,
            "PNG",
            x,
            y,
            logoWidth,
            logoHeight,
            undefined,
            "NONE"
          );
        });
      }
    },
  });

  doc.save("filtered-schools.pdf");
}

export function downloadExcel(filteredSchoolsWithRank) {
  if (filteredSchoolsWithRank.length === 0) {
    alert("No filtered data to export.");
    return;
  }
  const data = filteredSchoolsWithRank.map((school) => ({
    "Filtered Rank": school.filteredRank,
    Name: school.name,
    "Original Rank": school.rank,
    "City/Town": school.city,
    Type: school.NFTYPE,
    Gender: school.gender,
    "Age Range": school.ageRange,
    Pupils: school.pupilsKS4,
    "Grade 5+": school.grade5Plus,
    "Progress 8": school.progress8Score,
  }));

  const worksheet = XLSX.utils.json_to_sheet(data);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, "FilteredSchools");

  const wbout = XLSX.write(workbook, { bookType: "xlsx", type: "array" });

  const blob = new Blob([wbout], { type: "application/octet-stream" });
  saveAs(blob, "filtered-schools.xlsx");
}
